/**
 * LanguageSelector — Reusable language picker (11 Indian languages).
 *
 * Unlike LanguageSelectorPage (which is a full-screen first-login flow),
 * this is a pure controlled component that can render anywhere — most
 * commonly inside a BottomSheet triggered from a header icon.
 *
 * API:
 * - `value`: currently selected language code (e.g. 'en', 'hi')
 * - `onChange`: fires when user selects a language
 * - `onConfirm`: optional — fires when user taps "Apply". If omitted,
 *   onChange IS the commit (used in BottomSheet where Apply isn't needed).
 *
 * Save to: client/src/components/common/LanguageSelector.tsx
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Language {
  code: string;
  name: string;
  native: string;
  flag: string;
}

export const LANGUAGES: Language[] = [
  { code: 'en', name: 'English',   native: 'English',    flag: '🇮🇳' },
  { code: 'hi', name: 'Hindi',     native: 'हिंदी',        flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi',   native: 'मराठी',        flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati',  native: 'ગુજરાતી',     flag: '🇮🇳' },
  { code: 'ta', name: 'Tamil',     native: 'தமிழ்',        flag: '🇮🇳' },
  { code: 'te', name: 'Telugu',    native: 'తెలుగు',       flag: '🇮🇳' },
  { code: 'kn', name: 'Kannada',   native: 'ಕನ್ನಡ',       flag: '🇮🇳' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം',    flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi',   native: 'ਪੰਜਾਬੀ',      flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali',   native: 'বাংলা',       flag: '🇮🇳' },
  { code: 'or', name: 'Odia',      native: 'ଓଡ଼ିଆ',      flag: '🇮🇳' },
];

interface LanguageSelectorProps {
  value: string;
  onChange: (code: string) => void;
  /** Optional: shows Apply button when provided. Omit for instant-commit mode. */
  onConfirm?: (code: string) => void;
  /** When true, shows the support note below the grid. Default false. */
  showAINote?: boolean;
  /** Language codes to offer. I18N-T1: default to the ONLY languages we translate (en+hi). */
  codes?: string[];
}

export function LanguageSelector({
  value,
  onChange,
  onConfirm,
  showAINote = false,
  codes = ['en', 'hi'],
}: LanguageSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const shown = LANGUAGES.filter((l) => codes.includes(l.code));

  const handleApply = async () => {
    if (!onConfirm) return;
    setBusy(true);
    try {
      onConfirm(value);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* 2-column grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}
      >
        {shown.map((lang) => {
          const isSelected = value === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => onChange(lang.code)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 12,
                background: isSelected ? 'var(--p-lt)' : 'var(--surface)',
                border: `1.5px solid ${isSelected ? 'var(--p)' : 'var(--border)'}`,
                boxShadow: isSelected ? '0 0 0 3px rgba(79,70,229,0.08)' : 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: isSelected ? 'var(--p-md)' : 'var(--surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {lang.flag}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: isSelected ? 'var(--p)' : 'var(--t1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lang.native}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    color: isSelected ? 'var(--p-dk)' : 'var(--t3)',
                  }}
                >
                  {lang.name}
                </div>
              </div>
              {isSelected && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--p)" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* AI translation note */}
      {showAINote && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '10px 12px',
            background: 'var(--teal-lt)',
            border: '1px solid rgba(13,148,136,0.2)',
            borderRadius: 10,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--teal-dk)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
            <polygon points="12 2 2 7 12 12 22 7 12 2" />
            <polyline points="2 17 12 22 22 17" />
            <polyline points="2 12 12 17 22 12" />
          </svg>
          <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--teal-dk)', margin: 0, lineHeight: 1.5, fontFamily: 'var(--font-body)' }}>
            {t('common:language.note')}
          </p>
        </div>
      )}

      {/* Apply button (only if onConfirm provided) */}
      {onConfirm && (
        <button
          onClick={handleApply}
          disabled={busy}
          style={{
            width: '100%',
            height: 44,
            borderRadius: 12,
            border: 'none',
            background: busy ? 'var(--surface-2)' : 'linear-gradient(135deg, var(--p), var(--vi))',
            color: busy ? 'var(--t4)' : '#fff',
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'var(--font-body)',
            cursor: busy ? 'not-allowed' : 'pointer',
            boxShadow: busy ? 'none' : '0 2px 8px rgba(79,70,229,0.25)',
          }}
        >
          {busy ? t('common:loading.please_wait') : t('common:buttons.apply')}
        </button>
      )}
    </div>
  );
}

export default LanguageSelector;